import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../../context/AuthContext.jsx";
import { fetchReverseStock, fetchReverseStockDetails, returnToInventory } from "../../../../api/Inventory/ReverseStockApi.js";
import {
  getListColumns,
  getHistoryColumns,
  getDetailColumns,
  getFrontShopDetailColumns,
  getOtcDetailColumnsPending,
  getHistoryDetailColumns,
  getFrontShopHistoryDetailColumns,
  getOtcHistoryDetailColumns,
} from "./columns.jsx";

export default function useReverseStock() {
  const { user } = useAuth();
  const roleId = Number(user?.roleid);

  const navigate = useNavigate();
  const { scriptNo } = useParams();
  const urlTab = (() => {
    try {
      const qs = new URLSearchParams(window.location.search);
      return (qs.get('tab') || '').toUpperCase();
    } catch {
      return '';
    }
  })();

  const [mode, setMode] = useState("list");
  const [activeSegment, setActiveSegment] = useState("Dispensary");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 4;

  const [pendingRows, setPendingRows] = useState([]);
  const [historyRowsState, setHistoryRowsState] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);

  const [detailsData, setDetailsData] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const toggleSelectAll = () => {
    const next = !selectAll;
    setSelectAll(next);
    setDetailsData((prev) => prev.map((r) => ({ ...r, checked: next })));
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [mode, activeSegment, fromDate, toDate, urlTab]);

  // Keep mode aligned with URL tab to avoid bouncing back to Pending
  useEffect(() => {
    if (urlTab === 'NCND' && mode === 'list') {
      setMode('history');
    } else if ((urlTab === 'PENDING' || urlTab === '') && (mode === 'history' || mode === 'historyDetails')) {
      setMode('list');
    }
  }, [urlTab]);

  useEffect(() => {
    if (roleId === 6 && activeSegment !== "Clinic") {
      setActiveSegment("Clinic");
    }
  }, [roleId, activeSegment]);

  const toggleRow = (rowIndex) => {
    setDetailsData((prev) => {
      const copy = [...prev];
      copy[rowIndex] = { ...copy[rowIndex], checked: !copy[rowIndex].checked };
      const all = copy.every((r) => r.checked);
      setSelectAll(all);
      return copy;
    });
  };

  const columns = useMemo(
    () =>
      getListColumns((row) => {
        setSelectedRow(row);
        // Load details for this script in the current segment
        (async () => {
          try {
            const rxId = row?._raw?.prescriptionid ?? row?._raw?.PRESCRIPTIONID ?? null;
            const rowIsHistory = !!(row?._raw?.returnedtoinventory ?? row?._raw?.RETURNEDTOINVENTORY);
            const res = await fetchReverseStockDetails({ prescriptionid: rxId, department: activeSegment, tab: rowIsHistory ? "NCND" : "Pending" });
            const list = Array.isArray(res?.data) ? res.data : [];
            const mapped = list.map((d) => {
              const expiry = d?.expiry ? new Date(d.expiry) : null;
              const expStr = expiry ? formatDDMMYYYY(expiry) : "";
              if (activeSegment === "Front Shop") {
                return {
                  product: d?.product ?? "",
                  storage: d?.storage ?? "",
                  category: d?.category ?? "",
                  barcode: d?.barcode ?? "",
                  nappi: d?.nappi ?? "",
                  expiry: expStr,
                  checked: false,
                  returnid: d?.returnid,
                };
              }
              // Dispensary/OTC use drug fields
              return {
                drug: d?.product ?? "",
                storage: d?.storage ?? "",
                ingredient: d?.ingredient ?? "",
                nappi: d?.nappi ?? "",
                barcode: d?.barcode ?? "",
                expiry: expStr,
                checked: false,
                returnid: d?.returnid,
              };
            });
            setDetailsData(mapped);
          } catch (e) {
            setDetailsData([]);
          } finally {
            const rowIsHistory = !!(row?._raw?.returnedtoinventory ?? row?._raw?.RETURNEDTOINVENTORY);
            setMode(rowIsHistory ? "historyDetails" : "details");
            navigate(`/inventory/reverse-stock/${row.script}?tab=${rowIsHistory ? 'NCND' : 'Pending'}`);
          }
        })();
      }),
    [activeSegment, navigate, mode]
  );

  const historyColumns = useMemo(
    () =>
      getHistoryColumns((row) => {
        setSelectedRow(row);
        // Load NCND details for this script in the current segment
        (async () => {
          try {
            const rxId = row?._raw?.prescriptionid ?? row?._raw?.PRESCRIPTIONID ?? null;
            const res = await fetchReverseStockDetails({ prescriptionid: rxId, department: activeSegment, tab: 'NCND' });
            const list = Array.isArray(res?.data) ? res.data : [];
            const mapped = list.map((d) => {
              const expiry = d?.expiry ? new Date(d.expiry) : null;
              const expStr = expiry ? formatDDMMYYYY(expiry) : "";
              if (activeSegment === "Front Shop") {
                return {
                  product: d?.product ?? "",
                  storage: d?.storage ?? "",
                  category: d?.category ?? "",
                  barcode: d?.barcode ?? "",
                  nappi: d?.nappi ?? "",
                  expiry: expStr,
                  returnid: d?.returnid,
                };
              }
              return {
                drug: d?.product ?? "",
                storage: d?.storage ?? "",
                ingredient: d?.ingredient ?? "",
                nappi: d?.nappi ?? "",
                barcode: d?.barcode ?? "",
                expiry: expStr,
                returnid: d?.returnid,
              };
            });
            setDetailsData(mapped);
          } catch (e) {
            setDetailsData([]);
          } finally {
            setMode("historyDetails");
            navigate(`/inventory/reverse-stock/${row.script}?tab=NCND`);
          }
        })();
      }),
    [activeSegment, navigate]
  );

  const detailColumns = useMemo(() => getDetailColumns(detailsData, toggleRow), [detailsData]);
  const frontShopDetailColumns = useMemo(() => getFrontShopDetailColumns(detailsData, toggleRow), [detailsData]);
  const otcDetailColumnsPending = useMemo(() => getOtcDetailColumnsPending(detailsData, toggleRow), [detailsData]);
  const historyDetailColumns = useMemo(() => getHistoryDetailColumns(), []);
  const frontShopHistoryDetailColumns = useMemo(() => getFrontShopHistoryDetailColumns(), []);
  const otcHistoryDetailColumns = useMemo(() => getOtcHistoryDetailColumns(), []);

  // Parse dd/MM/yyyy to Date (local)
  const parseDDMMYYYY = (s) => {
    if (!s || typeof s !== "string") return null;
    const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s.trim());
    if (!m) return null;
    const dd = Number(m[1]);
    const mm = Number(m[2]);
    const yyyy = Number(m[3]);
    const d = new Date(yyyy, mm - 1, dd);
    return isNaN(d.getTime()) ? null : d;
  };

  const formatDDMMYYYY = (d) => {
    if (!(d instanceof Date)) return "";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  // Load rows from backend according to active segment and mode (respect ?tab=NCND in URL)
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const isHistory = urlTab === 'NCND' || mode === "history" || mode === "historyDetails";
        const params = {
          tab: isHistory ? "NCND" : "Pending",
          department: activeSegment,
          fromDate: fromDate ? fromDate.toISOString() : undefined,
          toDate: toDate ? toDate.toISOString() : undefined,
          page: 1,
          pageSize: 1000,
        };
        const res = await fetchReverseStock(params);
        const rows = Array.isArray(res?.data) ? res.data : [];
        // Map API rows to UI row shape
        const mapped = rows.map((r) => {
          const clinicReference = r?.clinicreference ?? r?.CLINICREFERENCE;
          const otcReference = r?.otcreference ?? r?.OTCREFERENCE;
          const psn = r?.processscriptnumber ?? r?.PROCESSSCRIPTNUMBER;
          const fallbackId = r?.prescriptionid ?? r?.PRESCRIPTIONID;
          const scriptReference = clinicReference || otcReference || psn;
          const script = scriptReference != null && scriptReference !== '' ? String(scriptReference) : String(fallbackId ?? '');
          const dateRaw = r?.returndate ?? r?.RETURNDATE;
          const d = dateRaw ? new Date(dateRaw) : null;
          // Prefer explicit counts when provided
          const pendingCount = r?.pending_count ?? r?.PENDING_COUNT;
          const ncndCount = r?.ncnd_count ?? r?.NCND_COUNT;
          const activeCount = (mode === 'history' ? ncndCount : pendingCount);
          const count = (activeCount != null ? activeCount : (r?.itemcount ?? r?.ITEMCOUNT ?? 0));
          return {
            script,
            issues: Number(count) || 0,
            date: d ? formatDDMMYYYY(d) : "",
            status: (r?.returnedtoinventory ?? r?.RETURNEDTOINVENTORY) ? "NCND" : "Pending",
            _raw: r,
          };
        });
        if (cancelled) return;
        if (isHistory) setHistoryRowsState(mapped);
        else setPendingRows(mapped);
      } catch (e) {
        if (!cancelled) {
          setPendingRows([]);
          setHistoryRowsState([]);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [mode, activeSegment, fromDate, toDate, urlTab]);

  // Filter history rows by fromDate/toDate inclusively
  const filteredHistoryRows = useMemo(() => {
    if (!fromDate && !toDate) return historyRowsState;
    return historyRowsState.filter((r) => {
      const d = parseDDMMYYYY(r.date);
      if (!d) return false;
      let ok = true;
      if (fromDate) {
        // compare start-of-day for fromDate
        const fd = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
        ok = ok && d >= fd;
      }
      if (toDate) {
        // inclusive end-of-day for toDate
        const td = new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate(), 23, 59, 59, 999);
        ok = ok && d <= td;
      }
      return ok;
    });
  }, [historyRowsState, fromDate, toDate]);

  // Keep view in sync with URL: when scriptNo is present, show details; when it's removed, go back to list
  useEffect(() => {
    // No scriptNo param -> ensure we are back on the list view
    if (!scriptNo) {
      if (mode !== "list" && mode !== "history") {
        setMode("list");
      }
      if (selectedRow) {
        setSelectedRow(null);
      }
      return;
    }

    // If we already derived state for this script, don't recompute
    if (selectedRow && selectedRow.script === scriptNo) return;

    const fromPending = pendingRows.find((r) => r.script === scriptNo);
    const fromHistory = historyRowsState.find((r) => r.script === scriptNo);

    if (!fromPending && !fromHistory) return;

    // Prefer history selection when user is in NCND context (mode or URL)
    if (fromHistory && (urlTab === 'NCND' || mode === "history" || mode === "historyDetails")) {
      setSelectedRow(fromHistory);
      setMode("historyDetails");
    } else if (fromPending) {
      setSelectedRow(fromPending);
      setMode("details");
    } else if (fromHistory) {
      setSelectedRow(fromHistory);
      setMode("historyDetails");
    }
  }, [scriptNo, mode, selectedRow, pendingRows, historyRowsState, activeSegment, urlTab]);

  const activeDataset = useMemo(() => {
    if (mode === "details") return detailsData;
    if (mode === "historyDetails") return detailsData;
    if (mode === "history") return filteredHistoryRows;
    return pendingRows;
  }, [mode, detailsData, filteredHistoryRows, pendingRows]);

  const totalPages = Math.max(1, Math.ceil((activeDataset?.length || 0) / pageSize));
  const pageData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return (activeDataset || []).slice(start, start + pageSize);
  }, [activeDataset, currentPage]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  const handleSegmentChange = (seg) => {
    // Always switch to Pending (list) when changing department
    setActiveSegment(seg);
    setMode("list");
    setSelectedRow(null);
    setDetailsData([]);
    setCurrentPage(1);
  };

  const handleReturnToStock = async () => {
    if (!selectedRow) return;
    const toReturn = detailsData.filter((r) => r.checked && r.returnid);
    if (!toReturn.length) return;
    try {
      // Execute returns sequentially to keep it simple
      for (const row of toReturn) {
        await returnToInventory(row.returnid, {});
      }
      // After success, reload NCND details for this script and keep NCND context
      const rxId = selectedRow?._raw?.prescriptionid ?? selectedRow?._raw?.PRESCRIPTIONID ?? null;
      if (rxId) {
        const res = await fetchReverseStockDetails({ prescriptionid: rxId, department: activeSegment, tab: 'NCND' });
        const list = Array.isArray(res?.data) ? res.data : [];
        const mapped = list.map((d) => {
          const expiry = d?.expiry ? new Date(d.expiry) : null;
          const expStr = expiry ? formatDDMMYYYY(expiry) : "";
          if (activeSegment === "Front Shop") {
            return {
              product: d?.product ?? "",
              storage: d?.storage ?? "",
              category: d?.category ?? "",
              barcode: d?.barcode ?? "",
              nappi: d?.nappi ?? "",
              expiry: expStr,
              returnid: d?.returnid,
            };
          }
          return {
            drug: d?.product ?? "",
            storage: d?.storage ?? "",
            ingredient: d?.ingredient ?? "",
            nappi: d?.nappi ?? "",
            barcode: d?.barcode ?? "",
            expiry: expStr,
            returnid: d?.returnid,
          };
        });
        setDetailsData(mapped);
        setSelectAll(false);
        setMode('historyDetails');
        navigate(`/inventory/reverse-stock/${selectedRow.script}?tab=NCND`);
      }
      // Also refresh lists implicitly by switching mode to history (effect will fetch NCND list)
    } catch (e) {
      // No-op for now; could show toast
    }
  };

  return {
    roleId,
    mode,
    setMode,
    activeSegment,
    handleSegmentChange,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    currentPage,
    setCurrentPage,
    pageData,
    totalPages,
    selectAll,
    toggleSelectAll,
    handleReturnToStock,
    // column sets
    columns,
    historyColumns,
    detailColumns,
    frontShopDetailColumns,
    otcDetailColumnsPending,
    historyDetailColumns,
    frontShopHistoryDetailColumns,
    otcHistoryDetailColumns,
  };
}
